<script lang="ts">
	import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { get_local_ip, real_id } from '../../../helpers/interface_helpers';
	import { onSettingsResponse } from '../../../helpers/device_settings_helpers';
	//Services
	import { localReq, postReq } from '../../../services/http';
	//Stores
	import { getDeviceStatus, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	//Design system components
	import { Checkbox, Button, Alert } from '@Shelly/shelly-design-system';
	//Components
	import NetworkModal from '../NetworkComponents/NetworkModal.svelte';
	import SsidForm from '../NetworkComponents/SSIDForm.svelte';
	//Types
	import type { SettingRequest } from '../../../types/requests/settings/setting';
	import type { Device } from '../../../types/Device';
	import { capFirstChar } from '../../../helpers/utilities';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';

	export let device: Device;
	export let isOffline;

	const id = real_id(device.id),
		settings = readDeviceSettings(id);

	let enabled = settings?.wifi_ap.enabled,
		waitForResponse = false,
		ssidForm: SsidForm,
		modal: NetworkModal,
		postReqData: {
			id: string;
			enabled: number;
			key?: string;
		};

	const connect = async () => {
			waitForResponse = true;
			let res: SettingRequest;
			const local_ip = get_local_ip(id);

			if (local_ip) {
				delete postReqData.id;
				res = await localReq(device, `${local_ip}/settings/ap`, 'post', postReqData, 6000, true);
			} else {
				//TODO need to check endpoint behavior because locally we can sent command without password so i think it will be right to do it
				res = await postReq(`/device/settings/wifi_ap`, postReqData);
			}
			if (res.isok) {
				devices_status.remove(id);
				devices_settings.remove(id);
			}
			onSettingsResponse(res, device, {
				success: $format('ap_scc_toast', {
					default: 'Access point settings saved.',
				}),
			});
			waitForResponse = false;
		},
		validate = () => {
			postReqData = {
				id,
				enabled: Number(enabled),
			};
			if (enabled) {
				const ssidParams = ssidForm.validate();
				if (!ssidParams) return false;
				postReqData.key = ssidParams.pass;
				modal.open();
			}
		};

	//TODO need to speak with Alex if device will or will not be removed while going on AP mode

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({ onClick: validate, loading: waitForResponse, disabled: isOffline });
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

{#if $showNewLayout}
	<Checkbox
		size="l"
		class="mb-s-plus"
		bind:checked={enabled}
		disabled={isOffline}
		id="checkbox-0jxkmct"
	>
		{capFirstChar($format('sub_settings_title_wifi_mode_ap', { default: 'access point' }))}
	</Checkbox>

	<Alert
		class="mb-s"
		type="warning"
		heading={$format('alert_heading_ap_password_recommendation', {
			default: 'Password Recommendation',
		})}
		isHeadingBold
		message={$format('alert_message_ap_password_recommendation', {
			default:
				"It is important to password protect your device's access point to secure your network from unauthorized access.",
		})}
	/>

	<SsidForm
		bind:this={ssidForm}
		ssid={settings?.wifi_ap.ssid}
		is_open_enabled={false}
		disableForm={!enabled || isOffline}
		readonly={true}
		withoutBackground
		removeBottomPadding
		removePaddings
	/>
{:else}
	<Checkbox size="l" class="mb-s" bind:checked={enabled} disabled={isOffline} id="checkbox-0jxkmct">
		{capFirstChar($format('option_enable', { default: 'enable' }))}
	</Checkbox>

	<hr class="mb-m mt-s" />

	<Alert
		class="mb-s"
		type="warning"
		heading={$format('alert_heading_ap_password_recommendation', {
			default: 'Password Recommendation',
		})}
		isHeadingBold
		message={$format('alert_message_ap_password_recommendation', {
			default:
				"It is important to password protect your device's access point to secure your network from unauthorized access.",
		})}
	/>

	<SsidForm
		bind:this={ssidForm}
		ssid={settings?.wifi_ap.ssid}
		is_open_enabled={false}
		disableForm={!enabled || isOffline}
		readonly={true}
		withoutBackground
	/>

	<Button size="m" width="full" on:click={validate} loading={waitForResponse} disabled={isOffline}>
		{$format('button_save', { default: 'save' })}
	</Button>
{/if}

<NetworkModal
	title={enabled
		? $format('enable_access_point_modal_title', {
				default: 'Enable Access Point?',
			})
		: $format('disable_access_point_mmodal_title', {
				default: 'Disable Access Point?',
			})}
	btnTexts={{
		cancelText: '',
		saveText: enabled ? $format('button_enable') : $format('disable'),
	}}
	bind:this={modal}
	on:confirm={connect}
	{isOffline}
>
	{$format('ap_prompt_text', {
		default:
			'"Device enable access point and disable Wi-Fi connection. You may lose connection with it. Are you sure?',
	})}
</NetworkModal>
